/**
 * Circuit Breaker Pattern
 * Prevents cascading failures by failing fast when service is down
 */

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: CircuitState = 'CLOSED';
  
  // Configuration
  private readonly failureThreshold = 5; // Open circuit after 5 failures
  private readonly resetTimeout = 60000; // Try again after 60 seconds
  private readonly successThreshold = 2; // Close after 2 successes in HALF_OPEN

  /**
   * Execute function through circuit breaker
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check circuit state
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime < this.resetTimeout) {
        throw new Error(
          'âš¡ Service temporarily unavailable. Circuit breaker is OPEN. ' +
          'Please try again in a minute.'
        );
      }
      // Timeout elapsed, try HALF_OPEN state
      this.state = 'HALF_OPEN';
}

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Handle successful request
   */
  private onSuccess(): void {
    this.failures = 0;

    if (this.state === 'HALF_OPEN') {
}

    this.state = 'CLOSED';
  }

  /**
   * Handle failed request
   */
  private onFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
      console.error(
        `ðŸ”´ Circuit breaker OPEN after ${this.failures} failures. ` +
        `Will retry in ${this.resetTimeout / 1000} seconds.`
      );
    }
  }

  /**
   * Get current circuit state (for monitoring)
   */
  getState() {
    return {
      state: this.state,
      failures: this.failures,
      lastFailureTime: this.lastFailureTime,
      isOpen: this.state === 'OPEN',
    };
  }

  /**
   * Manually reset circuit breaker
   */
  reset(): void {
    this.failures = 0;
    this.state = 'CLOSED';
}
}

// Singleton instance
export const circuitBreaker = new CircuitBreaker();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).__circuitBreaker = circuitBreaker;
}

